import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import {
  patternsApi,
  stylesApi,
  skillLevelsApi,
  instructorsApi,
  movesApi,
  adminVideosApi,
  getApiBaseUrl,
} from '../services/api';
import { Pattern, CreatePatternData, Style, SkillLevel, Instructor, Move } from '../types';

type PatternFormValues = Omit<CreatePatternData, 'moves'>;

type SlotDraft = { moveId: number; notes: string };

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const PatternsManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingPattern, setEditingPattern] = useState<Pattern | null>(null);
  const [search, setSearch] = useState('');
  const [styleFilter, setStyleFilter] = useState('');
  const [skillLevelFilter, setSkillLevelFilter] = useState('');
  const [instructorFilter, setInstructorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [slots, setSlots] = useState<SlotDraft[]>([]);
  const [addMoveId, setAddMoveId] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');
  const [currentThumbnailUrl, setCurrentThumbnailUrl] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { data: patternsData, isLoading } = useQuery({
    queryKey: ['patterns', { page, search, styleFilter, skillLevelFilter, instructorFilter, statusFilter }],
    queryFn: () =>
      patternsApi.getAll({
        page,
        limit: 10,
        search: search || undefined,
        styleId: styleFilter ? parseInt(styleFilter) : undefined,
        skillLevelId: skillLevelFilter ? parseInt(skillLevelFilter) : undefined,
        instructorId: instructorFilter ? parseInt(instructorFilter) : undefined,
        published: statusFilter === 'published' ? true : statusFilter === 'draft' ? false : undefined,
      }),
  });

  const { data: styles = [] } = useQuery({
    queryKey: ['styles-all'],
    queryFn: () => stylesApi.getAll({ limit: 100 }).then((res) => res.data),
  });
  const { data: skillLevels = [] } = useQuery({
    queryKey: ['skill-levels-all'],
    queryFn: () => skillLevelsApi.getAll({ limit: 100 }).then((res) => res.data),
  });
  const { data: instructors = [] } = useQuery({
    queryKey: ['instructors-all'],
    queryFn: () => instructorsApi.getAll({ limit: 100, status: 'active' }).then((res) => res.data),
  });
  const { data: allMoves = [] } = useQuery({
    queryKey: ['moves-all-for-patterns'],
    queryFn: () => movesApi.getAll({ limit: 1000, published: true }).then((res) => res.data),
  });

  const movesById = React.useMemo(() => {
    const m = new Map<number, Move>();
    for (const move of allMoves) m.set(move.id, move);
    return m;
  }, [allMoves]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PatternFormValues>();

  const titleValue = watch('title');

  useEffect(() => {
    if (!editingPattern && !slugTouched && titleValue) {
      setValue('slug', slugify(titleValue));
    }
  }, [titleValue, editingPattern, slugTouched, setValue]);

  const createMutation = useMutation({
    mutationFn: (data: CreatePatternData) => patternsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patterns'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      closeForm();
    },
    onError: (err: any) => {
      alert(err?.response?.data?.error || 'Failed to create pattern');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<PatternFormValues> }) =>
      patternsApi.update(id, data),
    onSuccess: async (_, { id }) => {
      // Also persist the moves list — simpler UX than splitting saves.
      await patternsApi.updateMoves(
        id,
        slots.map((s, i) => ({ moveId: s.moveId, position: i, notes: s.notes || null })),
      );
      queryClient.invalidateQueries({ queryKey: ['patterns'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      closeForm();
    },
    onError: (err: any) => {
      alert(err?.response?.data?.error || 'Failed to update pattern');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => patternsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patterns'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });

  const closeForm = () => {
    setShowForm(false);
    setEditingPattern(null);
    setSlots([]);
    setCurrentVideoUrl('');
    setCurrentThumbnailUrl('');
    setSlugTouched(false);
    reset();
  };

  const openCreate = () => {
    setEditingPattern(null);
    setSlots([]);
    setCurrentVideoUrl('');
    setCurrentThumbnailUrl('');
    setSlugTouched(false);
    reset({
      title: '',
      slug: '',
      description: '',
      videoUrl: '',
      thumbnail: '',
      duration: undefined,
      instructorId: null,
      styleId: null,
      skillLevelId: null,
      classContext: '',
      classDate: null,
      isPublished: false,
      accessLevel: 'free',
    });
    setShowForm(true);
  };

  const handleEdit = (pattern: Pattern) => {
    setEditingPattern(pattern);
    setCurrentVideoUrl(pattern.videoUrl || '');
    setCurrentThumbnailUrl(pattern.thumbnail || '');
    setSlots(
      (pattern.moves ?? []).map((pm) => ({ moveId: pm.moveId, notes: pm.notes ?? '' })),
    );
    setSlugTouched(true);
    reset({
      title: pattern.title,
      slug: pattern.slug,
      description: pattern.description ?? '',
      videoUrl: pattern.videoUrl ?? '',
      thumbnail: pattern.thumbnail ?? '',
      duration: pattern.duration ?? undefined,
      instructorId: pattern.instructorId ?? null,
      styleId: pattern.styleId ?? null,
      skillLevelId: pattern.skillLevelId ?? null,
      classContext: pattern.classContext ?? '',
      classDate: pattern.classDate ? pattern.classDate.slice(0, 10) : null,
      isPublished: pattern.isPublished,
      accessLevel: pattern.accessLevel ?? 'free',
    });
    setShowForm(true);
  };

  const handleDelete = (pattern: Pattern) => {
    if (window.confirm(`Delete pattern "${pattern.title}"? This also deletes its demonstrations.`)) {
      deleteMutation.mutate(pattern.id);
    }
  };

  const addSlot = () => {
    const id = parseInt(addMoveId);
    if (!id) return;
    setSlots((prev) => [...prev, { moveId: id, notes: '' }]);
    setAddMoveId('');
  };

  const moveSlot = (index: number, direction: -1 | 1) => {
    setSlots((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const removeSlot = (index: number) => {
    setSlots((prev) => prev.filter((_, i) => i !== index));
  };

  const updateSlotNotes = (index: number, notes: string) => {
    setSlots((prev) => prev.map((s, i) => (i === index ? { ...s, notes } : s)));
  };

  const handleVideoUpload = async (file: File) => {
    setUploading(true);
    try {
      const result = await adminVideosApi.uploadVideo(file);
      setCurrentVideoUrl(result.videoUrl);
      setValue('videoUrl', result.videoUrl);
      if (result.thumbnailUrl) {
        setCurrentThumbnailUrl(result.thumbnailUrl);
        setValue('thumbnail', result.thumbnailUrl);
      }
    } catch (e: any) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to upload video.');
    } finally {
      setUploading(false);
    }
  };

  const handleThumbnailUpload = async (file: File) => {
    setUploadingThumbnail(true);
    try {
      const result = await adminVideosApi.uploadThumbnail(file);
      setCurrentThumbnailUrl(result.thumbnailUrl);
      setValue('thumbnail', result.thumbnailUrl);
    } catch (e: any) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to upload thumbnail.');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const onSubmit = (data: PatternFormValues) => {

    const payload: CreatePatternData = {
      title: data.title,
      slug: data.slug || undefined,
      description: data.description || undefined,
      videoUrl: data.videoUrl || undefined,
      thumbnail: data.thumbnail || undefined,
      duration: data.duration ? Number(data.duration) : undefined,
      instructorId: data.instructorId ? Number(data.instructorId) : null,
      styleId: data.styleId ? Number(data.styleId) : null,
      skillLevelId: data.skillLevelId ? Number(data.skillLevelId) : null,
      classContext: data.classContext || undefined,
      classDate: data.classDate || null,
      isPublished: !!data.isPublished,
      accessLevel: data.accessLevel ?? 'free',
    };

    if (editingPattern) {
      updateMutation.mutate({ id: editingPattern.id, data: payload });
    } else {
      createMutation.mutate({
        ...payload,
        moves: slots.map((s, i) => ({ moveId: s.moveId, position: i, notes: s.notes || null })),
      });
    }
  };

  return (
    <div className="patterns-management">
      <div className="page-header">
        <h1>Patterns Management</h1>
        <button className="btn btn-primary" onClick={openCreate} disabled={showForm}>
          Add New Pattern
        </button>
      </div>

      {showForm && (
        <div className="form-container">
          <h2>{editingPattern ? 'Edit Pattern' : 'Create New Pattern'}</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="admin-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  {...register('title', { required: 'Title is required' })}
                  type="text"
                  id="title"
                  className={errors.title ? 'error' : ''}
                  placeholder="e.g. Tuesday Intermediate Combo"
                />
                {errors.title && <span className="error-text">{errors.title.message}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="slug">Slug</label>
                <input
                  {...register('slug')}
                  type="text"
                  id="slug"
                  placeholder="auto-generated from title"
                  onChange={(e) => {
                    setSlugTouched(true);
                    setValue('slug', e.target.value);
                  }}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="instructorId">Instructor</label>
                <select {...register('instructorId')} id="instructorId">
                  <option value="">None</option>
                  {instructors.map((i: Instructor) => (
                    <option key={i.id} value={i.id}>{i.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="styleId">Style</label>
                <select {...register('styleId')} id="styleId">
                  <option value="">None</option>
                  {styles.map((s: Style) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="skillLevelId">Skill Level</label>
                <select {...register('skillLevelId')} id="skillLevelId">
                  <option value="">None</option>
                  {skillLevels.map((l: SkillLevel) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="classContext">Class Context</label>
                <input
                  {...register('classContext')}
                  type="text"
                  id="classContext"
                  placeholder="e.g. Wed 7pm Intermediate"
                />
              </div>
              <div className="form-group">
                <label htmlFor="classDate">Class Date</label>
                <input {...register('classDate')} type="date" id="classDate" />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                {...register('description')}
                id="description"
                rows={3}
                placeholder="Notes about the combo, what it teaches, how it flows..."
              />
            </div>

            <div className="form-section">
              <h3>Move Sequence</h3>
              {slots.length === 0 && <p className="pattern-slots-empty">No moves yet. Patterns can be saved without a sequence — add moves later as your catalog grows.</p>}
              <ol className="pattern-moves-editor">
                {slots.map((slot, idx) => {
                  const move = movesById.get(slot.moveId);
                  return (
                    <li key={`${slot.moveId}-${idx}`} className="pattern-slot">
                      <div className="pattern-slot-row">
                        <strong>{move ? move.name : `Move #${slot.moveId}`}</strong>
                        {move?.style?.name && <span className="pattern-slot-style">({move.style.name})</span>}
                        <button type="button" className="btn btn-small btn-secondary" aria-label="Move up" onClick={() => moveSlot(idx, -1)} disabled={idx === 0}>↑</button>
                        <button type="button" className="btn btn-small btn-secondary" aria-label="Move down" onClick={() => moveSlot(idx, 1)} disabled={idx === slots.length - 1}>↓</button>
                        <button type="button" className="btn btn-small btn-danger" onClick={() => removeSlot(idx)}>Remove</button>
                      </div>
                      <input
                        type="text"
                        placeholder="Optional notes for this slot (e.g. '2x8 counts', 'on the 5')"
                        aria-label="Notes for this slot"
                        value={slot.notes}
                        onChange={(e) => updateSlotNotes(idx, e.target.value)}
                        className="pattern-slot-notes"
                      />
                    </li>
                  );
                })}
              </ol>

              <div className="pattern-add-move-row">
                <select value={addMoveId} onChange={(e) => setAddMoveId(e.target.value)} className="pattern-add-move-select" aria-label="Pick a move to add">
                  <option value="">Pick a move to add…</option>
                  {allMoves.map((m: Move) => (
                    <option key={m.id} value={m.id}>
                      {m.name}{m.style?.name ? ` — ${m.style.name}` : ''}
                    </option>
                  ))}
                </select>
                <button type="button" className="btn btn-secondary" onClick={addSlot} disabled={!addMoveId}>
                  Add Move
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Canonical Video</label>
              <div className="video-upload-section">
                <input
                  {...register('videoUrl')}
                  type="text"
                  placeholder="Video URL (or upload below)"
                  aria-label="Video URL"
                  onChange={(e) => {
                    setCurrentVideoUrl(e.target.value);
                    setValue('videoUrl', e.target.value);
                  }}
                />
                <div className="video-file-upload">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/mp4,video/mpeg,video/quicktime,video/x-msvideo,video/x-ms-wmv"
                    onChange={(e) => e.target.files?.[0] && handleVideoUpload(e.target.files[0])}
                    disabled={uploading}
                    className="file-input-hidden"
                    aria-label="Upload video file"
                  />
                  <button
                    type="button"
                    className="btn btn-secondary video-upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading…' : 'Choose Video File'}
                  </button>
                </div>
                {currentVideoUrl && (
                  <div className="current-video-preview">
                    <span className="video-url-display">{currentVideoUrl}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Thumbnail</label>
              <div className="video-upload-section">
                {currentThumbnailUrl && (
                  <img
                    src={currentThumbnailUrl.startsWith('/') ? `${getApiBaseUrl().replace('/api', '')}${currentThumbnailUrl}` : currentThumbnailUrl}
                    alt="Thumbnail preview"
                    className="pattern-thumb-preview"
                  />
                )}
                <div className="video-file-upload">
                  <input
                    ref={thumbnailInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => e.target.files?.[0] && handleThumbnailUpload(e.target.files[0])}
                    disabled={uploadingThumbnail}
                    className="file-input-hidden"
                    aria-label="Upload thumbnail image"
                  />
                  <button
                    type="button"
                    className="btn btn-secondary video-upload-btn"
                    onClick={() => thumbnailInputRef.current?.click()}
                    disabled={uploadingThumbnail}
                  >
                    {uploadingThumbnail ? 'Uploading…' : 'Upload Thumbnail'}
                  </button>
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="checkbox-label">
                  <input {...register('isPublished')} type="checkbox" defaultChecked={false} />
                  Published
                </label>
              </div>
              <div className="form-group">
                <label htmlFor="accessLevel">Who can watch</label>
                <select id="accessLevel" {...register('accessLevel')}>
                  <option value="free">Free: all signed-in students</option>
                  <option value="members">Members only: students with a plan</option>
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={createMutation.isPending || updateMutation.isPending}>
                {editingPattern ? 'Update' : 'Create'} Pattern
              </button>
              <button type="button" className="btn btn-secondary" onClick={closeForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-controls">
        <input type="text" placeholder="Search patterns…" aria-label="Search patterns" value={search} onChange={(e) => setSearch(e.target.value)} className="search-input" />
        <select value={styleFilter} onChange={(e) => setStyleFilter(e.target.value)} className="status-filter" aria-label="Filter by style">
          <option value="">All Styles</option>
          {styles.map((s: Style) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={skillLevelFilter} onChange={(e) => setSkillLevelFilter(e.target.value)} className="status-filter" aria-label="Filter by skill level">
          <option value="">All Levels</option>
          {skillLevels.map((l: SkillLevel) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <select value={instructorFilter} onChange={(e) => setInstructorFilter(e.target.value)} className="status-filter" aria-label="Filter by instructor">
          <option value="">All Instructors</option>
          {instructors.map((i: Instructor) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="status-filter" aria-label="Filter by status">
          <option value="">All Status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
      </div>

      {isLoading ? (
        <div className="loading">Loading patterns…</div>
      ) : patternsData && patternsData.data.length > 0 ? (
        <>
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Moves</th>
                  <th>Style</th>
                  <th>Level</th>
                  <th>Instructor</th>
                  <th>Demos</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {patternsData.data.map((pattern) => (
                  <tr key={pattern.id}>
                    <td>
                      <strong>{pattern.title}</strong>
                      {pattern.classContext && (
                        <div className="pattern-class-context">{pattern.classContext}</div>
                      )}
                    </td>
                    <td>{pattern.moves?.length ?? 0}</td>
                    <td>{pattern.style?.name ?? '—'}</td>
                    <td>{pattern.skillLevel?.name ?? '—'}</td>
                    <td>{pattern.instructor?.name ?? '—'}</td>
                    <td>{pattern._count?.demonstrations ?? 0}</td>
                    <td>
                      <span className={`status ${pattern.isPublished ? 'published' : 'draft'}`}>
                        {pattern.isPublished ? 'Published' : 'Draft'}
                      </span>
                      {pattern.accessLevel === 'members' && <span className="status members-only">Members only</span>}
                    </td>
                    <td>{new Date(pattern.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-small btn-secondary" onClick={() => handleEdit(pattern)}>Edit</button>
                        <button
                          className="btn btn-small btn-danger"
                          onClick={() => handleDelete(pattern)}
                          disabled={deleteMutation.isPending}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {patternsData.pagination.pages > 1 && (
            <div className="pagination">
              {Array.from({ length: patternsData.pagination.pages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  className={`btn btn-small ${pageNum === page ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <p>No patterns yet. Create your first class combo!</p>
        </div>
      )}
    </div>
  );
};

export default PatternsManagement;
